import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  SplineCurve,
  Vector2,
} from "three"

export type ZoneOfControlGeometryParameters = {
  splines: Array<Vector2[]>
  scale: number
  segments: number
  colors: number[]
}


/**
 * A geometry class that builds a triangle strip along
 * corresponding spline paths. Used for rendering
 * styled border bands or Zone of Control visuals.
 *
 * ```ts
 * const geometry = new ZoneOfControlGeometry(splineArray, scale, segments, color)
 * ```
 *
 * @augments BufferGeometry
 */
export default class ZoneOfControlGeometry extends BufferGeometry {
  type: string
  parameters: ZoneOfControlGeometryParameters

  constructor(
    splines: Array<Vector2[]>,
    scale: number,
    segments: number,
    colorNums: number[],
  ) {
    super()

    this.type = "ZoneOfControlGeometry"

    const splineCount = splines.length
    if (splineCount < 2) {
      throw new Error("At least two splines are required.")
    }

    const splineListLen = splines[0].length
    for (const spline of splines) {
      if (spline.length !== splineListLen) {
        throw new Error("All splines must have the same length.")
      }
    }

    this.parameters = { splines, scale, segments, colors: colorNums }

    const toVec2 = (tp: Vector2) => new Vector2(tp.x * scale, tp.y * scale)

    const curves = splines.map(list => new SplineCurve(list.map(toVec2)))
    const pointSets = curves.map(curve => curve.getPoints((splineListLen - 1) * segments))

    const indices: number[] = []
    const vertices: number[] = []
    const normals: number[] = []
    const uvs: number[] = []
    const colors: number[] = []

    const pointLen = pointSets[0].length
    const colorObjs = colorNums.map(c => new Color(c))

    for (let i = 0; i < pointLen; i++) {
      const u = i / (pointLen - 1)

      for (let j = 0; j < splineCount; j++) {
        const p = pointSets[j][i]
        vertices.push(p.x, p.y, 0)
        normals.push(0, 0, 1)
        uvs.push(u, j)

        // Alpha ramp from 1 to 0
        const alpha = 1 - j / (splineCount - 1)
        const { r, g, b } = colorObjs[j]
        colors.push(r, g, b, alpha)
      }

      if (i > 0) {
        const prevRowStart = (i - 1) * splineCount
        const currRowStart = i * splineCount

        for (let j = 0; j < splineCount - 1; j++) {
          const a = prevRowStart + j
          const b = prevRowStart + j + 1
          const c = currRowStart + j + 1
          const d = currRowStart + j

          indices.push(a, b, d)
          indices.push(b, c, d)
        }
      }
    }

    this.setIndex(indices)
    this.setAttribute("position", new Float32BufferAttribute(vertices, 3))
    this.setAttribute("normal", new Float32BufferAttribute(normals, 3))
    this.setAttribute("uv", new Float32BufferAttribute(uvs, 2))
    this.setAttribute("color", new Float32BufferAttribute(colors, 4))
  }

  copy(source: ZoneOfControlGeometry) {
    super.copy(source)
    this.parameters = { ...source.parameters }
    return this
  }

  static fromJSON(data: ZoneOfControlGeometryParameters) {
    return new ZoneOfControlGeometry(
      data.splines,
      data.scale,
      data.segments,
      data.colors,
    )
  }
}
